/**
 * Typed fetchers for /api/v1/audit/*.
 *
 * Types come from the OpenAPI-generated schema. Regenerate via
 * `pnpm gen:api` after a backend Pydantic change.
 */

import type { components } from "./_generated/schema";
import { apiFetch } from "./apiFetch";

// The generated schema lags behind the backend until `pnpm gen:api` is run.
// `actor_email` / `actor_display_name` were added to AuditEventOut (resolved
// at view time); bridge them here as optional so the SPA compiles now. Re-run
// `pnpm gen:api` to fold them into the generated type and drop this augment.
export type AuditEvent = components["schemas"]["AuditEventOut"] & {
  actor_email?: string | null;
  actor_display_name?: string | null;
};
export type AuditEventListOut = components["schemas"]["AuditEventListOut"];
export type VerifyResult = components["schemas"]["VerifyOut"];

export interface ListAuditEventsParams {
  // Query params aren't part of `components.schemas` — keep this shape
  // hand-written and aligned with the backend router's parameters.
  limit?: number;
  cursor?: string | null;
  action?: string | null;
  actor_user_id?: string | null;
  actor_email?: string | null;
  resource_type?: string | null;
  resource_id?: string | null;
  from?: string | null; // ISO-8601 UTC
  to?: string | null;
}

export function listAuditEvents(
  token: string,
  params: ListAuditEventsParams = {},
): Promise<AuditEventListOut> {
  const qs = new URLSearchParams();
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  if (params.cursor) qs.set("cursor", params.cursor);
  if (params.action) qs.set("action", params.action);
  if (params.actor_user_id) qs.set("actor_user_id", params.actor_user_id);
  if (params.actor_email) qs.set("actor_email", params.actor_email);
  if (params.resource_type) qs.set("resource_type", params.resource_type);
  if (params.resource_id) qs.set("resource_id", params.resource_id);
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  const tail = qs.toString();
  return apiFetch<AuditEventListOut>(`/api/v1/audit/events${tail ? `?${tail}` : ""}`, {
    token,
  });
}

export function verifyAuditChain(token: string): Promise<VerifyResult> {
  return apiFetch<VerifyResult>("/api/v1/audit/verify", { token });
}

/**
 * Admin-only: download the tenant's audit trail as CSV, honouring the same
 * filters as the list. Streams a file to disk. The backend records an
 * `audit.exported` event for the download itself.
 */
export async function downloadAuditTrail(
  token: string,
  params: ListAuditEventsParams = {},
): Promise<void> {
  const qs = new URLSearchParams();
  if (params.action) qs.set("action", params.action);
  if (params.actor_user_id) qs.set("actor_user_id", params.actor_user_id);
  if (params.actor_email) qs.set("actor_email", params.actor_email);
  if (params.resource_type) qs.set("resource_type", params.resource_type);
  if (params.resource_id) qs.set("resource_id", params.resource_id);
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  const tail = qs.toString();
  const res = await fetch(`/api/v1/audit/export${tail ? `?${tail}` : ""}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    throw new Error(`Export failed (HTTP ${res.status})`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "audit-trail.csv";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
